import {
  ChatBubbleIcon,
  CheckIcon,
  Cross2Icon,
  GearIcon,
  MagicWandIcon,
  PaperPlaneIcon,
  ReloadIcon,
  StopIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { useRouter } from '@tanstack/react-router';
import { Dialog } from 'radix-ui';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnswerText } from '@/components/assistant/answer-text';
import { AssistantSettingsDialog } from '@/components/assistant/settings-dialog';
import { SetupHelp } from '@/components/assistant/setup-help';
import { Callout } from '@/components/callout';
import { TONE_INK } from '@/components/tone';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { needsSetupHelp } from '@/lib/assistant/client';
import { extractFigures, unmatchedFigures } from '@/lib/assistant/figures';
import { useCurrentAssistantPage } from '@/lib/assistant/page-context';
import { serverLabel, useAssistantSettings } from '@/lib/assistant/settings';
import { cn } from '@/lib/utils';
import { EXPLAIN_THIS_PAGE, useAssistantChat, withDividers, type AnswerTurn } from './assistant-chat';

export const NOTE = 'Written by your local model from the figures on this page. Check before acting.';
export const NOT_WIRED = "I can explain the analytics pages; this one isn't wired up yet.";

/** After the answer ends: its figures that the brief (or the question) doesn't contain. */
function FigureCheck({ turn }: { turn: AnswerTurn }) {
  const unmatched = useMemo(
    () => unmatchedFigures(turn.text, [turn.brief.text, turn.question]),
    [turn.text, turn.brief.text, turn.question],
  );
  if (unmatched.length > 0) {
    return (
      <Callout tone="warning" title={`Not on this page: ${unmatched.join(', ')}`}>
        The model may have worked these out or made them up. Check them before using them.
      </Callout>
    );
  }
  if (extractFigures(turn.text).length === 0) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <CheckIcon aria-hidden className={TONE_INK.success} />
      Every figure in this answer is on this page.
    </p>
  );
}

function Answer({
  turn,
  last,
  canAsk,
  onStop,
  onRegenerate,
}: {
  turn: AnswerTurn;
  last: boolean;
  canAsk: boolean;
  onStop: () => void;
  onRegenerate: () => void;
}) {
  const streaming = turn.status === 'streaming';
  return (
    <article aria-label="Answer" aria-busy={streaming || undefined} className="space-y-2">
      {turn.text ? (
        <AnswerText text={turn.text} refs={turn.brief.refs} />
      ) : (
        streaming && (
          <p className="animate-pulse text-sm text-muted-foreground">
            {turn.thinking ? 'Thinking…' : 'Waiting for the model…'}
          </p>
        )
      )}
      {streaming && turn.text && turn.thinking && (
        <p className="animate-pulse text-xs text-muted-foreground">Thinking…</p>
      )}
      {turn.status === 'done' && <FigureCheck turn={turn} />}
      {turn.status === 'done' && turn.finishReason === 'length' && (
        <p className="text-xs text-muted-foreground">The model stopped at its length limit.</p>
      )}
      {turn.status === 'stopped' && <p className="text-xs text-muted-foreground">Stopped.</p>}
      {turn.status === 'error' && (
        <div className="space-y-2">
          <Callout tone="warning" title="The model didn't answer">
            {turn.error instanceof Error ? turn.error.message : 'Something went wrong.'}
          </Callout>
          {needsSetupHelp(turn.error) && <SetupHelp />}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {streaming && (
          <Button type="button" variant="outline" size="sm" onClick={onStop}>
            <StopIcon aria-hidden /> Stop
          </Button>
        )}
        {!streaming && last && (
          <Button type="button" variant="ghost" size="sm" onClick={onRegenerate} disabled={!canAsk}>
            <ReloadIcon aria-hidden /> {turn.status === 'error' ? 'Try again' : 'Regenerate'}
          </Button>
        )}
      </div>
    </article>
  );
}

/**
 * The floating assistant (docs/analytics-assistant.md): a button at the bottom right of every page
 * and a non-modal panel that stays open across navigation. Escape (from inside the panel) closes it
 * and focus returns to the button. The conversation lives here, so it lasts the session.
 */
export function AssistantPanel({ pathname, pageTitle }: { pathname: string; pageTitle: string }) {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [settings] = useAssistantSettings();
  const page = useCurrentAssistantPage();
  const chat = useAssistantChat({ settings, page, pathname, pageTitle });
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const descriptionId = useId();

  const items = withDividers(chat.turns, pathname, pageTitle);
  const pageNote = !settings ? null : !page ? NOT_WIRED : !page.brief ? (page.pending ?? null) : null;

  // New text keeps the log at the bottom only while the reader is there: scrolling up to read a
  // long answer stops the stream from pulling the view down. Opening the panel starts at the end.
  const atBottom = useRef(true);
  const router = useRouter();
  useEffect(() => {
    if (open) atBottom.current = true;
  }, [open]);
  useEffect(() => {
    const follow = () => {
      const log = logRef.current;
      if (log && atBottom.current) log.scrollTop = log.scrollHeight;
    };
    follow();
    // The router restores every element's scroll position after a navigation, the log's too:
    // follow again once it has.
    return router.subscribe('onRendered', follow);
  }, [router, open, chat.turns, pathname, pageNote]);

  const ask = (question: string) => {
    atBottom.current = true;
    chat.ask(question);
  };

  const send = () => {
    if (!draft.trim() || !chat.canAsk) return;
    ask(draft);
    setDraft('');
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen} modal={false}>
        <Dialog.Trigger asChild>
          <Button
            aria-label="Assistant"
            title="Assistant"
            size="icon-lg"
            className="fixed right-4 bottom-4 z-40 size-11! rounded-full! shadow-lg"
          >
            <ChatBubbleIcon aria-hidden className="size-5" />
          </Button>
        </Dialog.Trigger>
        <Dialog.Content
          ref={contentRef}
          aria-describedby={descriptionId}
          // Stays open while the user works on the page, clicks a nav link or opens a menu.
          onInteractOutside={(e) => e.preventDefault()}
          // Escape closes the panel only from inside it, not while the user types on the page.
          onEscapeKeyDown={(e) => {
            if (!contentRef.current?.contains(document.activeElement)) e.preventDefault();
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (inputRef.current && !inputRef.current.disabled ? inputRef.current : contentRef.current)?.focus();
          }}
          // A conversation gets the full height; the set-up offer only what it needs.
          className={cn(
            'fixed right-4 bottom-18 z-40 flex max-h-[calc(100dvh-6.5rem)] w-[min(27rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-xl outline-none',
            settings && 'h-[min(38rem,calc(100dvh-6.5rem))]',
          )}
        >
          <header className="flex items-start gap-1 border-b py-2 pr-2 pl-4">
            <div className="min-w-0 flex-1 py-1">
              <Dialog.Title className="text-sm font-semibold">Assistant</Dialog.Title>
              <Dialog.Description id={descriptionId} className="truncate text-xs text-muted-foreground">
                {settings ? `${settings.model} on ${serverLabel(settings.baseUrl)}` : 'Not set up on this device'}
              </Dialog.Description>
            </div>
            {chat.turns.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Clear the conversation"
                onClick={chat.clear}
              >
                <TrashIcon aria-hidden />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Assistant settings"
              onClick={() => setSettingsOpen(true)}
            >
              <GearIcon aria-hidden />
            </Button>
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="icon-sm" aria-label="Close the assistant">
                <Cross2Icon aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          {!settings ? (
            <div className="space-y-3 p-4 text-sm">
              <p className="font-medium">Set up a local model</p>
              <p className="text-muted-foreground">
                The assistant explains the analytics pages with a model that runs on this device, such as LM Studio or
                Ollama. The page's figures go from this browser to your model and nowhere else.
              </p>
              <Button type="button" onClick={() => setSettingsOpen(true)}>
                <GearIcon aria-hidden /> Set up the assistant…
              </Button>
            </div>
          ) : (
            <>
              <div
                ref={logRef}
                onScroll={(e) => {
                  const log = e.currentTarget;
                  atBottom.current = log.scrollHeight - log.clientHeight - log.scrollTop < 48;
                }}
                role="log"
                aria-label="Conversation"
                aria-busy={chat.streaming || undefined}
                className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4"
              >
                {chat.turns.length === 0 && !pageNote && (
                  <p className="text-sm text-muted-foreground">
                    Ask about the figures on this page, or start with {EXPLAIN_THIS_PAGE.toLowerCase()}.
                  </p>
                )}
                {items.map((turn) => {
                  if (turn.kind === 'divider') {
                    return (
                      <div key={turn.id} role="separator" aria-label={turn.label} className="flex items-center gap-2">
                        <span aria-hidden className="h-px flex-1 bg-border" />
                        <span aria-hidden className="text-xs text-muted-foreground">
                          {turn.label}
                        </span>
                        <span aria-hidden className="h-px flex-1 bg-border" />
                      </div>
                    );
                  }
                  if (turn.kind === 'user') {
                    return (
                      <p key={turn.id} className="ml-auto w-fit max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm">
                        <span className="sr-only">You: </span>
                        {turn.text}
                      </p>
                    );
                  }
                  return (
                    <Answer
                      key={turn.id}
                      turn={turn}
                      last={turn === chat.regenerable}
                      canAsk={chat.canAsk}
                      onStop={chat.stop}
                      onRegenerate={chat.regenerate}
                    />
                  );
                })}
                {pageNote && <p className="text-sm text-muted-foreground">{pageNote}</p>}
              </div>
              <div className="space-y-2 border-t p-3">
                <p className="text-xs text-muted-foreground">{NOTE}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!chat.canAsk}
                  onClick={() => ask(EXPLAIN_THIS_PAGE)}
                >
                  <MagicWandIcon aria-hidden /> {EXPLAIN_THIS_PAGE}
                </Button>
                <form
                  className="flex items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                >
                  <Textarea
                    ref={inputRef}
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    aria-label="Ask a follow-up question"
                    placeholder="Ask about the figures on this page"
                    disabled={!page?.brief}
                    className="max-h-32 resize-none"
                  />
                  <Button type="submit" size="icon" aria-label="Send" disabled={!chat.canAsk || !draft.trim()}>
                    <PaperPlaneIcon aria-hidden />
                  </Button>
                </form>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Root>
      <AssistantSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
